<script setup lang="ts">
import { ref } from 'vue'
import { api } from '@/api'
import { dateShortcuts } from '@/data/date-shortcuts'
import { ElMessage, ElMessageBox } from 'element-plus'
import dayjs from 'dayjs'

const language = ref<string>('')
const dateRange = ref<[Date | undefined, Date | undefined]>([undefined, undefined])
const previewCount = ref<number | null>(null)
const previewLoading = ref(false)
const sendLoading = ref(false)
const sendResult = ref<{ total: number; sent: number; failed: number } | null>(null)

const languages = [
  { value: '', label: 'All' },
  { value: 'en', label: 'English' },
  { value: 'ru', label: 'Russian' },
  { value: 'uk', label: 'Ukrainian' },
]

const buildPayload = () => ({
  from: dateRange.value[0]
    ? dayjs(dateRange.value[0]).startOf('day').toISOString()
    : undefined,
  to: dateRange.value[1]
    ? dayjs(dateRange.value[1]).endOf('day').toISOString()
    : undefined,
  language: language.value || undefined,
})

const onDryRun = async () => {
  previewLoading.value = true
  sendResult.value = null
  try {
    const result = await api.resendFirstTest.dryRun({ payload: buildPayload() })
    previewCount.value = result.total
  } catch {
    previewCount.value = null
  } finally {
    previewLoading.value = false
  }
}

const onSend = async () => {
  if (previewCount.value === null || previewCount.value === 0) {
    ElMessage.warning('Run dry run first')
    return
  }

  try {
    await ElMessageBox.confirm(
      `Resend test 1 video to ${previewCount.value} chatters?`,
      'Confirm',
      { confirmButtonText: 'Send', cancelButtonText: 'Cancel', type: 'warning' },
    )
  } catch {
    return
  }

  sendLoading.value = true
  try {
    sendResult.value = await api.resendFirstTest.execute({ payload: buildPayload() })
    ElMessage.success(`Done: ${sendResult.value.sent} sent, ${sendResult.value.failed} failed`)
    previewCount.value = null
  } catch {
    // handled by interceptor
  } finally {
    sendLoading.value = false
  }
}
</script>

<template>
  <main>
    <h2>Resend Test 1</h2>

    <el-card>
      <template #header>Find chatters who didn't pass test 1</template>

      <el-form label-position="top">
        <div style="display: flex; gap: 1em; flex-wrap: wrap">
          <el-form-item label="Language">
            <el-select v-model="language" style="width: 140px">
              <el-option
                v-for="lang in languages"
                :key="lang.value"
                :label="lang.label"
                :value="lang.value"
              />
            </el-select>
          </el-form-item>

          <el-form-item label="Assessment date range (optional)">
          <el-date-picker
            v-model="dateRange"
            type="daterange"
            start-placeholder="From"
            end-placeholder="To"
            :shortcuts="dateShortcuts"
            style="width: 350px"
          />
        </el-form-item>
        </div>

        <div style="display: flex; gap: 1em; align-items: center">
          <el-button type="info" :loading="previewLoading" @click="onDryRun">
            Dry Run
          </el-button>

          <span v-if="previewCount !== null" style="font-size: 1.1em">
            Found: <strong>{{ previewCount }}</strong> chatters
          </span>
        </div>

        <div v-if="previewCount" style="margin-top: 1.5em">
          <el-button type="warning" :loading="sendLoading" @click="onSend">
            Resend video + test to {{ previewCount }} chatters
          </el-button>
        </div>

        <el-descriptions
          v-if="sendResult"
          :column="3"
          border
          style="margin-top: 1.5em"
        >
          <el-descriptions-item label="Total">{{ sendResult.total }}</el-descriptions-item>
          <el-descriptions-item label="Sent">{{ sendResult.sent }}</el-descriptions-item>
          <el-descriptions-item label="Failed">{{ sendResult.failed }}</el-descriptions-item>
        </el-descriptions>
      </el-form>
    </el-card>
  </main>
</template>
